import React, { useContext, useEffect, useRef, useState } from 'react';
import MetadataBox from 'src/components/MessageGroupDetail/MetadataBox';
import SubmitModal from 'src/components/MessageGroupDetail/SubmitForTranslationModal/SubmitModal';
import RejectSourceReviewModal from 'src/components/MessageGroupDetail/RejectSourceReviewModal/RejectSourceReviewModal';
import StringVersionHistoryModal from 'src/components/MessageGroupDetail/StringVersionHistoryModal/StringVersionHistoryModal';
import { AuthContext } from 'src/components/auth/AuthContext';
import { ReactCellRenderer } from 'src/components/common/ui/CellRenderer/ReactCellRenderer';
import { useHistory, useLocation } from 'react-router-dom';
import { PopoverCellRenderer } from 'src/components/common/ui/CellRenderer/PopoverCellRenderer';
import {
  KatAlert,
  KatBreadcrumb,
  KatBreadcrumbItem,
  KatButton,
  KatDataGrid,
  KatPageHeader,
  KatPagination,
  KatPopover,
  KatTextarea,
} from '@amzn/katal-react';
import {
  ListStringsInStringGroupInput,
  SourceString,
  SourceStringVariant,
  StringGroup,
  useListStringsInStringGroupQuery,
} from 'src/generated/graphql';
import styles from './MessageGroupDetailView.module.scss';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import { GridApi, GridOptions } from 'ag-grid-community';
import type { DataGridConfigurationBuilder } from '@amzn/katal-components/es/data-grid/data-grid-configuration-builder';
import UpdateStringsModal from 'src/components/MessageGroupDetail/UpdateStringsModal/UpdateStringsModal';
import {
  getStringPK,
  isStepOneReview,
  variantToString,
} from 'src/views/SourceReview/MessageGroupDetailView/Utils';
import { StringIdHeaderComponent } from './StringIdHeaderComponent';

const MessageGroupDetailView = () => {
  const { t } = useTranslation();
  const { userId } = useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();
  const state = location.state as {
    stringGroup: StringGroup;
  };

  interface KatDataGridReadyEvent extends CustomEvent {
    detail: {
      configurationBuilder: DataGridConfigurationBuilder;
      api: GridApi;
    };
  }

  interface RowData {
    stringId: string[];
    stringKey: any;
    sourceText: string;
    context: string;
    reviewComment: string;
    stringPK: string;
  }

  // Modal and Pagination States
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [isUpdateStringsModalOpen, setIsUpdateStringsModalOpen] =
    useState<boolean>(false);
  const [isStringVersionHistoryModalOpen, setIsStringVersionHistoryModalOpen] =
    useState<boolean>(false);
  const [selectedStringKey, setSelectedStringKey] = useState<any>(null);

  // Track the current page number
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Track next page token
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(
    undefined
  );

  // Track page size
  const [itemsPerPage, setItemsPerPage] = useState<number>(30);

  // Store page tokens for each page
  const [pageTokens, setPageTokens] = useState<{
    [key: number]: string | undefined;
  }>({
    1: undefined,
  });

  // Popover states
  const [popoverContent, setPopoverContent] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [selectedData, setSelectedData] = useState<RowData | null>(null);
  type KatPopoverElement = React.ElementRef<typeof KatPopover>;
  const popoverRef = useRef<KatPopoverElement>(null);
  const gridApi = useRef<GridApi | null>(null);
  // Add this state for bulk edit mode
  const [isBulkEditMode, setIsBulkEditMode] = useState<boolean>(false);

  // Countdown state for redirecting to Review Source tab
  const [countdown, setCountdown] = useState(10);

  // Set input for ListStringsInStringGroup query
  const [queryInput, setQueryInput] = useState<ListStringsInStringGroupInput>({
    stringGroupName: state?.stringGroup?.stringGroupName || '',
    limit: itemsPerPage,
    nextPageToken: nextPageToken,
  });

  /* Gql Hooks*/
  const { data, loading, error, refetch } = useListStringsInStringGroupQuery({
    variables: { params: queryInput },
    errorPolicy: 'all',
    skip: !state?.stringGroup,
  });

  // Countdown and redirect while denying direct access to page
  useEffect(() => {
    if (!state?.stringGroup) {
      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      const timer = setTimeout(() => {
        history.push('/sourceReview');
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timer);
      };
    }
  }, [history, state?.stringGroup]);

  // Update page tokens
  useEffect(() => {
    const token = data?.listStringsInStringGroup?.nextPageToken || undefined;
    setNextPageToken(token);
    setPageTokens((prev) => ({
      ...prev,
      [currentPage + 1]: token,
    }));
  }, [data, currentPage]);

  const [reset, setReset] = useState<boolean>(false);

  const [stringResources, setStringResources] = useState<
    Map<string, SourceString> | undefined
  >(undefined);

  useEffect(() => {
    // group the strings by the string PK, so that it is possible to get the original source string when edited.
    const strings = data?.listStringsInStringGroup?.strings;
    if (strings) {
      const stringResourceByStringPKMap = new Map<string, SourceString>();
      strings?.forEach((string) => {
        stringResourceByStringPKMap.set(
          getStringPK(string as SourceString),
          string as SourceString
        );
      });

      setStringResources(stringResourceByStringPKMap);
    }
  }, [data, reset]);

  // Transform and manage row data
  const [rowData, setRowData] = useState<any[] | undefined>(undefined);

  useEffect(() => {
    if (stringResources) {
      const newRowData = Array.from(stringResources.entries()).map(
        ([key, string]) => {
          const stringId = [];
          stringId.push(string?.stringKey?.stringName);
          if (string?.stringKey?.variant) {
            const variant = variantToString(
              string?.stringKey.variant as SourceStringVariant[]
            );
            stringId.push(variant);
          }
          return {
            stringId: stringId,
            stringKey: string?.stringKey,
            sourceText: string?.headRevision?.content?.value,
            context: string?.headRevision?.content?.note,
            reviewComment: string?.headRevision?.reviewComment,
            stringPK: key,
          };
        }
      );
      setRowData(newRowData);
    }
  }, [stringResources]);

  const [updatedStrings, setUpdatedStrings] = useState<Record<string, RowData>>(
    {}
  );

  // Direct access denied alert
  if (!state?.stringGroup) {
    return (
      <div>
        <KatAlert
          className="source-review-message-group-page-no-direct-access-message"
          header={t(
            'source-review-message-group-page-no-direct-access-message-title'
          )}
          description={t(
            'source-review-message-group-page-no-direct-access-message-desc',
            { countdown: countdown }
          )}
          variant="warning"
        ></KatAlert>
      </div>
    );
  }

  const stringGroup = state.stringGroup;

  // Handle page change
  const handlePageChange = (e: any) => {
    const selectedPage = e.target.page as number;
    const totalPages = Object.keys(pageTokens).length;

    if (selectedPage > 0 && selectedPage <= totalPages) {
      setCurrentPage(selectedPage);
      setQueryInput({
        ...queryInput,
        nextPageToken: pageTokens[selectedPage],
      });
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setNextPageToken(undefined);
    setPageTokens({ 1: undefined });

    // Update query with new limit and reset page token
    setQueryInput({
      ...queryInput,
      limit: newItemsPerPage,
      nextPageToken: undefined,
    });
  };

  // Estimate total items for open-ended pagination
  const calculateTotalItems = () => {
    /* We have k+1 tokens when on kth page
         If we're on the last page and there's no next token,
         return exactly enough items to show current pages */
    if (!nextPageToken) {
      return (Object.keys(pageTokens).length - 1) * itemsPerPage;
    }
    // Otherwise, add one more page to indicate there are more pages
    return Object.keys(pageTokens).length * itemsPerPage;
  };

  const handleCellClick = (
    value: string,
    nodeId: string,
    field: string,
    data: RowData
  ) => {
    setPopoverContent(value);
    setSelectedNodeId(nodeId);
    setSelectedField(field);
    setSelectedData(data);
  };

  // Grid Columns
  const columns = [
    {
      field: 'sourceText',
      headerName: t(
        'source-review-message-group-detail-page-source-text-column'
      ),
      cellRenderer: ReactCellRenderer,
      cellRendererParams: {
        Component: PopoverCellRenderer,
        onCellClick: handleCellClick,
      },
    },
    {
      field: 'context',
      headerName: t(
        'source-review-message-group-detail-page-context-text-column'
      ),
      cellRenderer: ReactCellRenderer,
      cellRendererParams: {
        Component: PopoverCellRenderer,
        onCellClick: handleCellClick,
      },
    },
    {
      field: 'reviewComment',
      headerName: t(
        'source-review-message-group-detail-page-review-comment-column'
      ),
      cellRenderer: ReactCellRenderer,
      cellRendererParams: {
        Component: PopoverCellRenderer,
        onCellClick: handleCellClick,
      },
    },
  ];

  const errorOverlayHtml = `
    <div style="display: flex; align-items: center; position: relative; width: fit-content; justify-items: center;">
      <div style="width: 20px; height: 20px; border-radius: 50%; background-color: red; color: white; display: flex; align-items: center; justify-content: center; font-family: Arial, sans-serif;">!</div>
      <div style="color: red; padding-left: 5px;">
        ${t('source-review-message-group-detail-page-error')}
      </div>
    </div>`;

  // To update row data when the popover is closed
  const updateRowData = (nodeId: string, field: string, value: string) => {
    setRowData((prevRowData) => {
      return prevRowData?.map((row) => {
        if (row.stringId.join() === nodeId) {
          return { ...row, [field]: value };
        }
        return row;
      });
    });
  };

  const stringTable = () => {
    const isLoading = loading || error != undefined;
    const loadingText = loading
      ? t('source-review-message-group-detail-page-loading')
      : undefined;
    const handleGridReady = (e: KatDataGridReadyEvent) => {
      gridApi.current = e.detail.api;

      const gridElement = e.target as any;
      const gridContainer = gridElement.querySelector('.ag-grid-container');

      // Add a flag to track previous bulk mode state
      let previousIsBulkMode = false;

      // Observe class changes on the grid container
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'class'
          ) {
            const element = mutation.target as HTMLElement;
            const isBulkMode = element.classList.contains('bulk-editing-grid');
            // Only process if the bulk mode state has actually changed
            if (isBulkMode !== previousIsBulkMode) {
              setIsBulkEditMode(isBulkMode);

              if (isBulkMode) {
                // Clear the updateStrings when entering bulk edit mode
                setUpdatedStrings({});
              } else {
                // reset when exiting the bulk edit mode, regardless of save or cancel
                setReset((prevState) => !prevState);
              }

              // Update the previous state
              previousIsBulkMode = isBulkMode;
            }
          }
        });
      });

      if (gridContainer) {
        observer.observe(gridContainer, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      // Clean up observer when component unmounts
      return () => {
        observer.disconnect();
      };
    };

    let gridOptions: GridOptions = {
      defaultColDef: {
        sortable: false,
        editable: false,
        suppressMenu: true,
        headerClass: [styles.tableRow],
        suppressMovable: true,
      },
      singleClickEdit: false,
      suppressPaginationPanel: true,
      treeData: true,
      getDataPath: (data: RowData) => data.stringId,
      autoGroupColumnDef: {
        headerComponent: StringIdHeaderComponent,
        headerComponentParams: {
          displayName: t(
            'source-review-message-group-detail-page-string-id-column'
          ),
        },
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          'white-space': 'normal',
          'word-break': 'break-word',
        },
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: (params: any) => {
            return `<kat-link variant="primary">${params.value}</kat-link>`;
          },
        },
      },
      onCellClicked: (params) => {
        if (params.column.getColId() === 'ag-Grid-AutoColumn') {
          setSelectedStringKey(params.data?.stringKey);
          setIsStringVersionHistoryModalOpen(true);
        }
      },
      rowClass: [styles.tableRow],
    };

    if (error) {
      gridOptions = {
        ...gridOptions,
        overlayLoadingTemplate: errorOverlayHtml,
      };

      if (gridApi.current) {
        gridApi.current.showLoadingOverlay();
      }
    }

    return (
      <KatDataGrid
        className={styles.customDatagrid}
        clientSideData
        columns={columns}
        autoHeight={true}
        rowData={rowData}
        pagination
        pageSize={itemsPerPage}
        primaryKeyField="stringId"
        bulkEdit={true}
        isLoading={isLoading}
        loadingText={loadingText}
        gridOptions={gridOptions}
        onGridReady={handleGridReady}
        onRowsEdited={(_: any) => {
          if (Object.keys(updatedStrings).length != 0) {
            setIsUpdateStringsModalOpen(true);
          }
        }}
      >
        <div slot="custom-controls">
          <KatButton
            label={t('source-review-rejection-button-text')}
            variant="danger"
            size="base"
            className={styles.customControlsButton}
            disabled={isBulkEditMode}
            onClick={() => setIsRejectModalOpen(true)}
          ></KatButton>
          <KatButton
            label={t(
              isStepOneReview(stringGroup)
                ? 'source-review-message-group-detail-page-approve-button'
                : 'source-review-message-group-detail-page-submit-button'
            )}
            variant="primary"
            size="base"
            className={styles.customControlsButton}
            disabled={isBulkEditMode}
            onClick={() => setIsModalOpen(true)}
          ></KatButton>
        </div>
      </KatDataGrid>
    );
  };

  return (
    <>
      <KatBreadcrumb>
        <KatBreadcrumbItem
          label={t('source-review-page-title')}
          href="/sourceReview"
        ></KatBreadcrumbItem>
        <KatBreadcrumbItem
          label={stringGroup.stringGroupName?.toString() || ''}
          href="#"
        ></KatBreadcrumbItem>
      </KatBreadcrumb>
      <KatPageHeader header={stringGroup.stringGroupName?.toString() || ''} />
      <MetadataBox stringGroup={stringGroup} />
      {stringTable()}
      {!loading && (
        <div className={styles.paginationContainer}>
          <div className={styles.paginationWrapper}>
            <KatPagination
              id="message-group-pagination"
              itemsPerPage={itemsPerPage}
              onChange={handlePageChange}
              openEnded={!!nextPageToken}
              page={currentPage}
              totalItems={calculateTotalItems()}
            />
          </div>
          <div id="message-group-items-per-page">
            {t('source-review-message-group-detail-page-results-per-page')}
            &nbsp;
            <select
              id="message-group-items-per-page-select"
              value={itemsPerPage.toString()}
              onChange={handleItemsPerPageChange}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
            </select>
          </div>
        </div>
      )}
      <SubmitModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        stringGroup={stringGroup}
        userAlias={userId}
        isBulkEditMode={isBulkEditMode}
        onSuccess={(approvalStatus) => {
          setTimeout(() => {
            history.replace('/sourceReview', {
              approvalStatus,
              messageGroupName: stringGroup.stringGroupName,
            });
          }, 0);
        }}
      />

      <RejectSourceReviewModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        stringGroupName={stringGroup.stringGroupName || ''}
        requestId={stringGroup.requestId || ''}
        operatorAlias={userId || ''}
        onSuccess={() => {
          setTimeout(() => {
            history.replace('/sourceReview');
          }, 0);
        }}
      />

      <KatPopover ref={popoverRef} id="popover-column-id" position="bottom">
        {isBulkEditMode ? (
          <div>
            <KatTextarea
              value={popoverContent}
              className="bulk-update-textarea"
              onChange={(e: CustomEvent) => {
                if (gridApi.current && selectedNodeId && selectedField) {
                  const newValue = (e.target as HTMLTextAreaElement).value;
                  const node = gridApi.current.getRowNode(selectedNodeId);
                  // set the text in popover textarea back to the data grid
                  if (node) {
                    setPopoverContent(newValue);
                    node.setDataValue(selectedField, newValue);
                    updateRowData(selectedNodeId, selectedField, newValue);
                    if (selectedData) {
                      setUpdatedStrings((prevState) => ({
                        ...prevState,
                        [selectedData.stringPK]: selectedData,
                      }));
                    }
                  }
                }
              }}
            />{' '}
          </div>
        ) : (
          <pre className={styles.popoverReadOnly}>{popoverContent}</pre>
        )}
      </KatPopover>

      {/* Update Strings Modal */}
      <UpdateStringsModal
        isOpen={isUpdateStringsModalOpen}
        onClose={() => {
          setIsUpdateStringsModalOpen(false);
          setUpdatedStrings({});
        }}
        onSuccess={() => {
          refetch();
        }}
        updatedStrings={updatedStrings}
        stringResources={stringResources!}
      />

      {/* String Version History Modal */}
      <StringVersionHistoryModal
        isOpen={isStringVersionHistoryModalOpen}
        onClose={() => setIsStringVersionHistoryModalOpen(false)}
        stringKey={selectedStringKey}
      />

      {/* Header Info Popover */}
      <KatPopover id="string-id-header-popover" position="top">
        {t('source-review-string-id-header-popover-text')}
      </KatPopover>
    </>
  );
};
export default MessageGroupDetailView;
